import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandingAssets, BrandingAssetType, TenantType } from '@/types/branding.types';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS, type IconName } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { uploadBrandingAsset, deleteBrandingAsset } from '@/services/branding.service';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { toast } from '@/utils/toast';
import { DEFAULT_IMAGES } from '@/constants/whitelabel-defaults';
import { devLog } from '@/utils/logger';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

/**
 * ImageSettings Component
 *
 * Manages whitelabel image/asset uploads
 * 12 customizable image slots organized by category
 * (Favicon, PWA assets, Email logo, and Loader remain system defaults)
 */

interface ImageSettingsProps {
  tenantType: TenantType;
  tenantId: number;
  assets: BrandingAssets;
  assetSources?: Record<string, 'team' | 'agency' | 'default'>;
  onChange: (assets: BrandingAssets) => void;
}

interface AssetConfig {
  key: keyof BrandingAssets;
  assetType: BrandingAssetType;
  label: string;
  description: string;
  category: 'logos' | 'headers' | 'misc';
  recommendedSize?: string;
  acceptedFormats?: string;
  defaultImage: string;
  canvaUrl?: string;
}

const ASSET_CONFIGS: AssetConfig[] = [
  // Logos (labels will be translated in renderAssetCard)
  {
    key: 'logoBig',
    assetType: BrandingAssetType.LOGO_BIG,
    label: 'settings_image_logo_big',
    description: 'settings_image_logo_big_desc',
    category: 'logos',
    recommendedSize: '200x60px',
    acceptedFormats: 'PNG, SVG',
    defaultImage: DEFAULT_IMAGES.logo_big,
  },
  {
    key: 'logoSmall',
    assetType: BrandingAssetType.LOGO_SMALL,
    label: 'settings_image_logo_small',
    description: 'settings_image_logo_small_desc',
    category: 'logos',
    recommendedSize: '60x60px',
    acceptedFormats: 'PNG, SVG',
    defaultImage: DEFAULT_IMAGES.logo_small,
  },

  // Headers
  {
    key: 'dashboardHeader',
    assetType: BrandingAssetType.DASHBOARD_HEADER,
    label: 'settings_image_dashboard_header',
    description: 'settings_image_dashboard_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.dashboard_header,
    canvaUrl: 'https://www.canva.com/design/DAGZqJ-m0dU/edit',
  },
  {
    key: 'calendarHeader',
    assetType: BrandingAssetType.CALENDAR_HEADER,
    label: 'settings_image_calendar_header',
    description: 'settings_image_calendar_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.calendar_header,
    canvaUrl: 'https://www.canva.com/design/DAGZqGBW0Yo/edit',
  },
  {
    key: 'listHeader',
    assetType: BrandingAssetType.LIST_HEADER,
    label: 'settings_image_list_header',
    description: 'settings_image_list_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.list_header,
    canvaUrl: 'https://www.canva.com/design/DAGZqJX7EF4/edit',
  },
  {
    key: 'brandcheckHeader',
    assetType: BrandingAssetType.BRANDCHECK_HEADER,
    label: 'settings_image_brand_check_header',
    description: 'settings_image_brand_check_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.brandcheck_header,
    canvaUrl: 'https://www.canva.com/design/DAGZqCK6hq0/edit',
  },
  {
    key: 'releaseHeader',
    assetType: BrandingAssetType.RELEASE_HEADER,
    label: 'settings_image_release_header',
    description: 'settings_image_release_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.release_header,
    canvaUrl: 'https://www.canva.com/design/DAGZqP25Oq8/edit',
  },
  {
    key: 'agencyccHeader',
    assetType: BrandingAssetType.AGENCYCC_HEADER,
    label: 'settings_image_brand_portfolio_header',
    description: 'settings_image_brand_portfolio_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.agencycc_header,
    canvaUrl: 'https://www.canva.com/design/DAGZqL9mnxw/edit',
  },
  {
    key: 'approvalHeader',
    assetType: BrandingAssetType.APPROVAL_HEADER,
    label: 'settings_image_release_board_header',
    description: 'settings_image_release_board_header_desc',
    category: 'headers',
    recommendedSize: '1920x200px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.approval_header,
  },

  // Footer
  {
    key: 'dashboardFooter',
    assetType: BrandingAssetType.DASHBOARD_FOOTER,
    label: 'settings_image_dashboard_footer',
    description: 'settings_image_dashboard_footer_desc',
    category: 'headers',
    recommendedSize: '1920x100px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.dashboard_footer,
    canvaUrl: 'https://www.canva.com/design/DAGZqD-O9mk/edit',
  },

  // Misc
  {
    key: 'placeholder',
    assetType: BrandingAssetType.PLACEHOLDER,
    label: 'settings_image_placeholder',
    description: 'settings_image_placeholder_desc',
    category: 'misc',
    recommendedSize: '400x400px',
    acceptedFormats: 'PNG, JPG',
    defaultImage: DEFAULT_IMAGES.placeholder,
    canvaUrl: 'https://www.canva.com/design/DAGZqPONNy0/edit',
  },
  {
    key: 'ogImage',
    assetType: BrandingAssetType.OG_IMAGE,
    label: 'settings_image_social_share',
    description: 'settings_image_social_share_desc',
    category: 'misc',
    recommendedSize: '1200x630px',
    acceptedFormats: 'JPG, PNG',
    defaultImage: DEFAULT_IMAGES.og_image,
  },
];

export const ImageSettings = ({ tenantType, tenantId, assets, assetSources, onChange }: ImageSettingsProps) => {
  const { t } = useTranslation('settings');
  const [uploadingAssets, setUploadingAssets] = useState<Set<BrandingAssetType>>(new Set());
  const [previewImage, setPreviewImage] = useState<{ url: string; label: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AssetConfig | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileSelect = async (config: AssetConfig, file: File) => {
    // Translate label for toast messages
    const label = config.label.startsWith('settings_image_') ? t(config.label) : config.label;

    // Validate file type (MIME)
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif', 'image/x-icon'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('settings_image_error_file_type'));
      return;
    }

    // Validate file extension (defense in depth)
    const validExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.webp'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast.error(t('settings_image_error_file_type'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(t('settings_image_error_file_size'));
      return;
    }

    setUploadingAssets((prev) => new Set(prev).add(config.assetType));

    try {
      const result = await uploadBrandingAsset(tenantType, tenantId, config.assetType, file);

      // URL is unique per upload (timestamp in S3 key) — no cache-busting needed
      const updatedAssets = {
        ...assets,
        [config.key]: result.url,
      };
      onChange(updatedAssets);

      toast.success(t('settings_image_success_upload', { label }));
    } catch (error) {
      devLog.error('Upload error:', error);
      toast.error(t('settings_image_error_upload', { label }));
    } finally {
      setUploadingAssets((prev) => {
        const next = new Set(prev);
        next.delete(config.assetType);
        return next;
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    const config = deleteConfirm;
    const label = config.label.startsWith('settings_image_') ? t(config.label) : config.label;

    setIsDeleting(true);
    try {
      await deleteBrandingAsset(tenantType, tenantId, config.assetType);

      // Remove asset from state
      const updatedAssets = { ...assets };
      delete updatedAssets[config.key];
      onChange(updatedAssets);

      toast.success(t('settings_image_success_delete', { label }));
      setDeleteConfirm(null);
    } catch (error) {
      devLog.error('Delete error:', error);
      toast.error(t('settings_image_error_delete', { label }));
    } finally {
      setIsDeleting(false);
    }
  };

  const getAssetsByCategory = (category: AssetConfig['category']) => {
    return ASSET_CONFIGS.filter((c) => c.category === category);
  };

  const renderAssetCard = (config: AssetConfig) => {
    const assetUrl = assets[config.key];
    const displayUrl = assetUrl || config.defaultImage;
    // Use backend source info if available, otherwise fall back to URL comparison
    const isCustomImage = assetSources
      ? assetSources[config.key] === 'team' || assetSources[config.key] === 'agency'
      : !!assetUrl && assetUrl !== config.defaultImage;
    const isUploading = uploadingAssets.has(config.assetType);

    // Translate label if it's a translation key, otherwise use as-is
    const label = config.label.startsWith('settings_image_') ? t(config.label) : config.label;
    const description = config.description.startsWith('settings_image_') ? t(config.description) : config.description;

    return (
      <div
        key={config.key}
        className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-900">{label}</label>
              <Tooltip
                content={
                  <div>
                    <p className="font-medium">{description}</p>
                    {config.recommendedSize && (
                      <p className="text-xs text-gray-300 mt-1">
                        {t('settings_image_recommended_size', { size: config.recommendedSize })}
                      </p>
                    )}
                    {config.acceptedFormats && (
                      <p className="text-xs text-gray-300">{t('settings_image_formats_list', { formats: config.acceptedFormats })}</p>
                    )}
                  </div>
                }
              >
                <Icon
                  name={ICONS.INFO}
                  size="xs"
                  color="text-gray-400"
                  className="cursor-help"
                  ariaHidden
                />
              </Tooltip>
            </div>
            {config.recommendedSize && (
              <p className="text-xs text-gray-500 mt-1">{config.recommendedSize}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                isCustomImage
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isCustomImage ? t('settings_image_badge_custom') : t('settings_image_badge_default')}
            </span>
            {config.canvaUrl && (
              <a
                href={config.canvaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <Icon name={'brush-paint' as IconName} size="xs" ariaHidden />
                {t('settings_image_canva_edit', { defaultValue: 'Edit in Canva' })}
              </a>
            )}
          </div>
        </div>

        {/* Preview Area */}
        <div className="mb-3">
          <div className="relative group">
            <img
              src={displayUrl}
              alt={label}
              className="w-32 h-32 object-contain bg-gray-50 rounded-xl border border-gray-200 mx-auto"
            />

            {/* Hover Overlay */}
            <div className="absolute inset-0 flex items-center justify-center gap-1.5 rounded-xl bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:mx-auto [@media(hover:none)]:max-w-32 [@media(hover:none)]:flex-wrap [@media(hover:none)]:content-center [@media(hover:none)]:bg-transparent [@media(hover:none)]:opacity-100">
              <Tooltip content={t('settings_image_preview_button')} side="bottom">
                <button
                  type="button"
                  onClick={() => setPreviewImage({ url: displayUrl, label })}
                  aria-label={t('settings_image_preview_button')}
                  className="flex p-2 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] items-center justify-center bg-white/90 text-gray-900 rounded-lg hover:bg-white transition-colors"
                >
                  <Icon name={ICONS.EYE} size="sm" ariaHidden />
                </button>
              </Tooltip>
              <Tooltip content={isCustomImage ? t('settings_image_replace_button') : t('settings_image_upload_button')} side="bottom">
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[config.key]?.click()}
                  disabled={isUploading}
                  aria-label={isCustomImage ? t('settings_image_replace_button') : t('settings_image_upload_button')}
                  className="flex p-2 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] items-center justify-center bg-white/90 text-gray-900 rounded-lg hover:bg-white transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />
                  ) : (
                    <Icon name={ICONS.UPLOAD} size="sm" ariaHidden />
                  )}
                </button>
              </Tooltip>
              {isCustomImage && (
                <Tooltip content={t('settings_image_delete_button')} side="bottom">
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(config)}
                    aria-label={t('settings_image_delete_button')}
                    className="flex p-2 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] items-center justify-center bg-red-600/90 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={(el) => (fileInputRefs.current[config.key] = el)}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFileSelect(config, file);
            }
            // Reset input so same file can be selected again
            e.target.value = '';
          }}
          className="hidden"
          aria-label={t('settings_image_upload_aria', { label })}
        />
      </div>
    );
  };

  const categories: Array<{
    id: AssetConfig['category'];
    title: string;
    description: string;
  }> = [
    {
      id: 'logos',
      title: t('settings_image_category_logos'),
      description: t('settings_image_category_logos_desc'),
    },
    {
      id: 'headers',
      title: t('settings_image_category_headers'),
      description: t('settings_image_category_headers_desc'),
    },
    {
      id: 'misc',
      title: t('settings_image_category_misc'),
      description: t('settings_image_category_misc_desc'),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('settings_image_title')}</h2>
        <p className="text-sm text-gray-600 mt-1">
          {t('settings_image_subtitle')}
        </p>
      </div>

      {/* Info Note */}
      <div className="bg-soft-mint border border-light-teal-gray rounded-xl p-4">
        <div className="flex gap-3">
          <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden />
          <div className="flex-1">
            <p className="text-sm font-medium text-dark-teal">{t('settings_image_info_title')}</p>
            <ul className="text-sm text-dark-teal mt-2 space-y-1 list-disc list-inside">
              <li>{t('settings_image_guideline_size')}</li>
              <li>{t('settings_image_guideline_formats')}</li>
              <li>{t('settings_image_guideline_transparency')}</li>
              <li>{t('settings_image_guideline_dimensions')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Asset Categories */}
      {categories.map((category) => (
        <div key={category.id} className="space-y-4">
          {/* Category Header */}
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg p-4 border border-gray-100">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">{category.title}</h3>
            <p className="text-sm text-gray-600">{category.description}</p>
          </div>

          {/* Asset Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getAssetsByCategory(category.id).map(renderAssetCard)}
          </div>
        </div>
      ))}

      {/* Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{previewImage.label}</h3>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('settings_image_close_preview')}
              >
                <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 bg-gray-100">
              <img
                src={previewImage.url}
                alt={previewImage.label}
                className="max-w-full max-h-[70vh] object-contain mx-auto rounded-lg"
              />
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <Button variant="secondary" size="sm" onClick={() => setPreviewImage(null)}>
                {t('settings_image_modal_close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        open={!!deleteConfirm}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title={t('settings_image_confirm_delete_title', { defaultValue: 'Delete Image' })}
        description={t('settings_image_confirm_delete_desc', {
          defaultValue: 'This will remove the custom image and revert to the default. This action cannot be undone.',
        })}
        confirmText={t('settings_image_delete_button')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};
